import { useEffect, useState } from 'react';

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      language_code?: string;
    };
  };
  ready: () => void;
  close: () => void;
  expand: () => void;
  MainButton: {
    setText: (text: string) => void;
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  };
  themeParams: Record<string, string>;
  colorScheme: 'light' | 'dark';
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

export function useTelegram(): {
  webApp: TelegramWebApp | null;
  initData: string;
  userId: number | null;
  isReady: boolean;
  haptic: (kind: 'light' | 'medium' | 'heavy', type?: 'success' | 'error' | 'warning') => void;
  showBack: (cb: () => void) => void;
  hideBack: () => void;
  mainButton: (text: string, cb: () => void) => void;
  hideMainButton: () => void;
} {
  const [webApp, setWebApp] = useState<TelegramWebApp | null>(null);
  const [initData, setInitData] = useState<string>('');
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const fakeInitData = () =>
      `query_id=test&user=${encodeURIComponent(
        JSON.stringify({
          id: 123456,
          first_name: 'Dev',
          last_name: 'User',
          username: 'devuser',
          language_code: 'uz',
        }),
      )}&auth_date=${Math.floor(Date.now() / 1000)}&hash=fakehash`;

    const tg = window.Telegram?.WebApp;
    if (tg) {
      try {
        tg.ready();
        tg.expand();
      } catch {
        // noop
      }
      setWebApp(tg);
      const realInitData = tg.initData || '';
      setInitData(realInitData || fakeInitData());
      setIsReady(true);
    } else {
      setInitData(fakeInitData());
      setIsReady(true);
    }
  }, []);

  const userId = webApp?.initDataUnsafe?.user?.id ?? null;

  const haptic = (
    kind: 'light' | 'medium' | 'heavy',
    type?: 'success' | 'error' | 'warning',
  ) => {
    try {
      if (type) webApp?.HapticFeedback?.notificationOccurred(type);
      else webApp?.HapticFeedback?.impactOccurred(kind);
    } catch {
      // noop
    }
  };

  const showBack = (cb: () => void) => {
    if (!webApp) return;
    webApp.BackButton.show();
    webApp.BackButton.onClick(cb);
  };
  const hideBack = () => {
    if (!webApp) return;
    webApp.BackButton.hide();
  };
  const mainButton = (text: string, cb: () => void) => {
    if (!webApp) {
      console.info('[MainButton]', text, '-> press to call', cb);
      return;
    }
    webApp.MainButton.setText(text);
    webApp.MainButton.show();
    webApp.MainButton.onClick(cb);
  };
  const hideMainButton = () => {
    webApp?.MainButton.hide();
  };

  return { webApp, initData, userId, isReady, haptic, showBack, hideBack, mainButton, hideMainButton };
}
